import React from 'react';
import { formatCalculatedStat, type CalculatedStats } from '../../../../utils/statCalculations';

/**
 * Shared Tennis stat-tile presentation (sc-339 follow-up). One place for the
 * tile look and the friendly labels, reused everywhere a Tennis stat line
 * shows up: the match detail page, the post-match summary, and the player
 * card's season Stats tab. Raw `stat_key`s (ServePts_1stIn, etc.) never reach
 * the UI directly — every tile here has a short, human label.
 */

export interface TennisTileDef {
  /** Key into raw totals, or into `calc` when `fromCalc` is set. */
  key: string;
  label: string;
  fromCalc?: boolean;
}

export const SERVE_TILES: TennisTileDef[] = [
  { key: 'FirstServePct', label: '1st Serve %', fromCalc: true },
  { key: 'FirstServeWinPct', label: '1st Serve Win %', fromCalc: true },
  { key: 'SecondServeWinPct', label: '2nd Serve Win %', fromCalc: true },
  { key: 'Aces', label: 'Aces' },
  { key: 'DoubleFaults', label: 'Double Faults' },
  { key: 'HoldPct', label: 'Hold %', fromCalc: true },
];

export const RETURN_TILES: TennisTileDef[] = [
  { key: 'RetPtsWon', label: 'Return Pts Won' },
  { key: 'BreakPct', label: 'Break %', fromCalc: true },
  { key: 'BreakPtsWon', label: 'Break Pts Won' },
  { key: 'BreakPtsFaced', label: 'Break Pts Faced' },
  { key: 'BreakPtsSaved', label: 'Break Pts Saved' },
];

export const SHOT_QUALITY_TILES: TennisTileDef[] = [
  { key: 'Winners', label: 'Winners' },
  { key: 'UnforcedErrors', label: 'Unforced Errors' },
  { key: 'ForcedErrors', label: 'Forced Errors' },
  { key: 'WUERatio', label: 'Winner/UE Ratio', fromCalc: true },
];

export const COURT_POSITION_TILES: TennisTileDef[] = [
  { key: 'NetPts', label: 'Net Points' },
  { key: 'NetPtsWon', label: 'Net Points Won' },
  { key: 'NetWinPct', label: 'Net Win %', fromCalc: true },
];

/** One tile: value on top, a short label below that's allowed to wrap onto two lines rather than truncate/overlap. */
export function TennisStatTile({ label, value }: { label: string; value: string }) {
  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 px-2 py-3 min-h-[68px] flex flex-col items-center justify-center text-center">
      <span className="text-base font-bold text-gray-900 dark:text-gray-50 tabular-nums leading-none">{value}</span>
      <span className="text-[11px] text-gray-500 dark:text-gray-400 mt-1.5 leading-tight">{label}</span>
    </div>
  );
}

/** A titled grid of tiles. 3 columns max — enough width per tile for a 2-3 word label to wrap cleanly instead of colliding. */
export function TennisStatSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 mb-2 uppercase tracking-wide">{title}</h4>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">{children}</div>
    </div>
  );
}

function tileValue(def: TennisTileDef, totals: Record<string, number>, calc: CalculatedStats): string {
  if (def.fromCalc) {
    return calc[def.key] !== undefined ? formatCalculatedStat(calc[def.key], def.key) : '—';
  }
  return String(totals[def.key] ?? 0);
}

export function TennisTileRow({ defs, totals, calc }: { defs: TennisTileDef[]; totals: Record<string, number>; calc: CalculatedStats }) {
  return (
    <>
      {defs.map((def) => (
        <TennisStatTile key={def.key} label={def.label} value={tileValue(def, totals, calc)} />
      ))}
    </>
  );
}

/**
 * The four stat-category sections every Tennis stat display shares (Serve,
 * Return & Pressure, Shot Quality, Court Position). Callers add their own
 * "record" section on top (a single match's score vs. a season's W/L), since
 * that's the one part that genuinely differs by context.
 */
export function TennisStatCategorySections({ totals, calc }: { totals: Record<string, number>; calc: CalculatedStats }) {
  return (
    <div className="space-y-4">
      <TennisStatSection title="Serve">
        <TennisTileRow defs={SERVE_TILES} totals={totals} calc={calc} />
      </TennisStatSection>
      <TennisStatSection title="Return & Pressure">
        <TennisTileRow defs={RETURN_TILES} totals={totals} calc={calc} />
      </TennisStatSection>
      <TennisStatSection title="Shot Quality">
        <TennisTileRow defs={SHOT_QUALITY_TILES} totals={totals} calc={calc} />
      </TennisStatSection>
      <TennisStatSection title="Court Position">
        <TennisTileRow defs={COURT_POSITION_TILES} totals={totals} calc={calc} />
      </TennisStatSection>
    </div>
  );
}
